import React from 'react';
import { Allotment } from 'allotment';
import 'allotment/dist/style.css';
import { XIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { getSidePanelRenderer, useSidePanel } from './SidePanel';
import { useCompactSidebar } from './useCompactSidebar';

type Children = React.ReactNode | ((state: { isSplit: boolean }) => React.ReactNode);

/**
 * Lays the conversation out beside whatever a tool call has asked to show.
 *
 * The split is always mounted and the second pane is hidden until something
 * claims it. That is not a style preference. Rendering `children` bare when
 * nothing is open and wrapping it in a pane when something is puts the
 * conversation at a different position in the element tree, and React answers
 * that by unmounting it: opening a dashboard emptied the thread it was opened
 * from. For a conversation whose id is not in the URL yet — every brand new one
 * — that is permanent, because there is nothing on the server to refetch. A
 * hidden pane costs one wrapper div; the remount cost the conversation.
 *
 * It is also what lets Thread's own re-anchor work. That effect watches its
 * viewport for width changes and scrolls back to the newest message, which only
 * means anything if the viewport survives the split opening.
 *
 * `children` may be a function, which receives whether the split is on. The
 * conversation needs that to drop its own history rail: two columns of chrome
 * either side of a third of the screen leaves very little message.
 */
export const SidePanelLayout: React.FC<{ children: Children }> = ({ children }) => {
  const panel = useSidePanel();
  const content = panel?.content ?? null;
  const Renderer = content ? getSidePanelRenderer(content.kind) : undefined;
  // A kind nothing registered a renderer for must not shrink the conversation
  // to sit beside an empty column.
  const isSplit = !!content && !!Renderer;

  // Trade the navigation column for an icon rail while the split is up, and put
  // it back when the panel closes.
  useCompactSidebar(isSplit);

  const body = typeof children === 'function' ? children({ isSplit }) : children;

  return (
    <Allotment defaultSizes={[1, 2]} separator>
      {/* Min width keeps the composer usable rather than letting the chat be
          dragged down to a slit. */}
      <Allotment.Pane minSize={320} preferredSize="33%">
        <div className="h-full w-full overflow-hidden">{body}</div>
      </Allotment.Pane>
      <Allotment.Pane minSize={420} visible={isSplit}>
        {/* No title bar. Everything shown here heads itself — a dashboard puts
            its name and description at the top of the panel — so a second copy
            of the name directly above it was pure duplication. The close
            control floats into the space that heading leaves on its right. */}
        <div className="relative h-full w-full border-l border-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => panel?.close()}
            aria-label={content?.title ? `Close ${content.title}` : 'Close panel'}
            className="absolute top-2 right-2 z-20"
          >
            <XIcon className="h-4 w-4" />
          </Button>
          <div className="h-full w-full overflow-hidden">
            {content && Renderer ? <Renderer id={content.id} title={content.title} /> : null}
          </div>
        </div>
      </Allotment.Pane>
    </Allotment>
  );
};
